import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Block } from '../../../../../../types/shared/blocks';
import {
	Box,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Radio,
	RadioGroup,
	Stack,
	styled,
	TextField,
	Typography,
} from '@mui/material';
import FeetInchesInput from '../FeetInchesInputV2';
import {
	MAX_DECK_HEIGHT,
	MAX_DECK_DEPTH,
	MAX_DECK_WIDTH,
} from '../../../../../../constants/limits';
import EdgeLabel from '../../../../../../enums/edgeLabel';
import { useDesignSpanContext } from '../../context/DesignSpanContext.tsx';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CustomSwitch from 'src/globalComponents/toggleButton.tsx';
import { toastError } from 'src/Toaster/Toast.tsx';

const StyledLabel = styled(Typography)({
	fontWeight: 500,
	fontSize: 14,
});

const DeckboxPopper: React.FC<{
	externalBlockData: Block;
	blockData: Block;
	setBlockData: Dispatch<SetStateAction<Block>>;
}> = ({ externalBlockData, blockData, setBlockData }) => {
	const { maxSpanIn } = useDesignSpanContext();
	const [qtyLock, setQtyLock] = useState(true);
	const { reface } = useQuoteState();

	useEffect(() => {
		console.log(externalBlockData.dropbeam);

		setBlockData({ ...blockData, dropbeam: externalBlockData.dropbeam });
	}, [externalBlockData.dropbeam]);

	const handleWidthChange = (inches) => {
		if (inches > MAX_DECK_WIDTH) return;
		const updatedData = {
			...blockData,
			dimensions: {
				width: inches,
				depth: blockData.dimensions.depth,
				height: blockData.dimensions.height,
			},
			displayDimensions: {
				width: inches,
				height: blockData.dimensions.depth,
			},
			edges: blockData.edges.map((edge) => {
				if (edge.label == EdgeLabel.TOP || edge.label == EdgeLabel.BOTTOM) {
					return {
						...edge,
						length: inches,
					};
				}
				return edge;
			}),
		};
		setBlockData(updatedData);
	};

	const handleDepthChange = (inches) => {
		if (inches > MAX_DECK_DEPTH) return;
		const updatedData = {
			...blockData,
			dimensions: {
				width: blockData.dimensions.width,
				depth: inches,
				height: blockData.dimensions.height,
			},
			displayDimensions: {
				width: blockData.dimensions.width,
				height: inches,
			},
			edges: blockData.edges.map((edge) => {
				if (edge.label == EdgeLabel.LEFT || edge.label == EdgeLabel.RIGHT) {
					return {
						...edge,
						length: inches,
					};
				}
				return edge;
			}),
		};
		setBlockData(updatedData);
	};

	const handleHeightChange = (inches) => {
		if (inches > MAX_DECK_HEIGHT) return;
		const updatedData = {
			...blockData,
			dimensions: {
				width: blockData.dimensions.width,
				depth: blockData.dimensions.depth,
				height: inches,
			},
		};
		setBlockData(updatedData);
	};

	const dropbeamsNeeded =
		maxSpanIn && !reface
			? Math.ceil(blockData.dimensions.depth / maxSpanIn - 1)
			: 0;

	const handleDropbeamChange = (e) => {
		const numberOfBoards = e.target.value;

		const updatedData = {
			...blockData,
			dropbeam: {
				isIncluded: true,
				numberOfBoards: parseInt(numberOfBoards),
				quantity: blockData.dropbeam?.quantity || dropbeamsNeeded || 1,
			},
		};
		setBlockData(updatedData);
	};

	const handleDropbeamToggle = (event) => {
		const isChecked = event.target.checked;
		const updatedDropbeam = {
			isIncluded: isChecked,
			numberOfBoards: isChecked
				? blockData.dropbeam?.numberOfBoards || 2
				: null,
			quantity: isChecked ? 1 : null,
		};

		const updatedData = {
			...blockData,
			dropbeam: updatedDropbeam,
		};
		setBlockData(updatedData);
	};

	console.log(blockData);

	return (
		<Stack direction="column" spacing={1.25}>
			<FeetInchesInput
				valueInches={blockData.dimensions.depth}
				labelInches="Depth"
				onChange={handleDepthChange}
				maxFeet={MAX_DECK_DEPTH / 12}
				toastLabel="Deckbox Depth"
			/>
			<FeetInchesInput
				valueInches={blockData.dimensions.width}
				labelInches="Width"
				onChange={handleWidthChange}
				maxFeet={MAX_DECK_WIDTH / 12}
				minFeet={2}
				toastLabel="Deckbox Width"
			/>
			<FeetInchesInput
				valueInches={blockData.dimensions.height}
				labelInches="Height"
				onChange={handleHeightChange}
				locked={false}
				maxFeet={MAX_DECK_HEIGHT / 12}
				toastLabel="Deckbox Height"
			/>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'start',
				}}
			>
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						gap: '10px',
						alignItems: 'center',
						paddingLeft: '30px',
					}}
				>
					<CustomSwitch
						onChange={handleDropbeamToggle}
						on={blockData.dropbeam?.isIncluded || false}
						size="small"
						disabled={dropbeamsNeeded > 0}
					/>

					<StyledLabel>Include Dropbeam?</StyledLabel>
				</Box>

				{blockData.dropbeam?.isIncluded && (
					<>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '5px',
							}}
						>
							<StyledLabel>How many dropbeams?</StyledLabel>

							<IconButton onClick={() => setQtyLock(!qtyLock)}>
								{!qtyLock && (
									<LockOpenIcon
										sx={{
											color: '#0090FF',
											fontSize: '20px',
										}}
									/>
								)}
								{qtyLock && (
									<LockIcon
										sx={{
											color: '#0090FF',
											fontSize: '20px',
										}}
									/>
								)}
							</IconButton>
						</Box>

						<TextField
							value={blockData.dropbeam?.quantity}
							sx={{ marginY: '10px' }}
							type="number"
							disabled={qtyLock}
							onChange={(e) => {
								let value = parseInt(e.target.value);

								if (value < dropbeamsNeeded) {
									toastError({
										title: 'Number of dropbeams',
										description: `${blockData.label} needs a minimum of ${dropbeamsNeeded} dropbeam(s)`,
									});

									value = dropbeamsNeeded;
								}
								setBlockData({
									...blockData,
									dropbeam: {
										isIncluded: true,
										numberOfBoards: blockData.dropbeam?.numberOfBoards || 2,
										quantity: value,
									},
								});
							}}
							slotProps={{
								htmlInput: {
									step: 1,
									inputMode: 'numeric',
									min: dropbeamsNeeded.toString() || '1',
								},
								input: {
									endAdornment: (
										<InputAdornment
											position="end"
											sx={{
												marginRight: '-10px',
											}}
										>
											<Typography color="gray.500" fontSize="12px">
												Quantity
											</Typography>
										</InputAdornment>
									),
								},
							}}
						/>

						<StyledLabel>
							<InfoOutlineIcon /> {dropbeamsNeeded} dropbeam minimum required
						</StyledLabel>
					</>
				)}

				{blockData.dropbeam?.isIncluded && (
					<RadioGroup
						value={blockData.dropbeam?.numberOfBoards}
						onChange={handleDropbeamChange}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'start',
							}}
						>
							<FormControlLabel
								value={2}
								control={<Radio />}
								label={<StyledLabel>Double</StyledLabel>}
							/>
							<FormControlLabel
								value={3}
								control={<Radio />}
								label={<StyledLabel>Triple</StyledLabel>}
							/>
						</Box>
					</RadioGroup>
				)}
			</Box>
		</Stack>
	);
};

export default DeckboxPopper;
